/** @graphql agg **/
import { useCallback, useSyncExternalStore } from 'react';

import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { TWCG_1489_CONNECTOR_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import type { PromoDismissed } from '../connector-promo-config-types.ts';
import type {
	useConnectorOnboardingStateQuery$data,
	useConnectorOnboardingStateQuery$variables,
} from './__generated__/useConnectorOnboardingStateQuery.graphql.ts';

export type ConnectorOnboardingState = {
	enrolmentDate: string | undefined;
	spotlightShown: boolean;
	promoDismissed: PromoDismissed | undefined;
};

type ConnectorOnboardingStateUpdate = {
	key: string;
	value: string;
};

export type UseConnectorOnboardingStateResult = {
	onboardingState: ConnectorOnboardingState | undefined;
	loading: boolean;
	error?: Error;
	updateOnboardingState: (state: ConnectorOnboardingStateUpdate) => void;
};

type ConnectorOnboardingStateEntries = NonNullable<
	NonNullable<useConnectorOnboardingStateQuery$data['userPreferences']>['onboardingState']
>;

const EMPTY_ENTRIES: ConnectorOnboardingStateEntries = [];

const connectorOnboardingStateQuery = graphql`
	query useConnectorOnboardingStateQuery {
		userPreferences {
			onboardingState(
				key: ["twcg-1489:enrolmentDate", "twcg-1489:spotlightShown", "twcg-1489:promoDismissed"]
			) {
				key
				value
			}
		}
	}
`;

const localOnboardingStateListeners = new Set<() => void>();
let localOnboardingState: Partial<ConnectorOnboardingState> = {};

const subscribeToLocalOnboardingState = (listener: () => void) => {
	localOnboardingStateListeners.add(listener);
	return () => localOnboardingStateListeners.delete(listener);
};

const getLocalOnboardingStateSnapshot = () => localOnboardingState;

const setLocalOnboardingState = (nextState: Partial<ConnectorOnboardingState>) => {
	localOnboardingState = nextState;
	localOnboardingStateListeners.forEach((listener) => listener());
};

/**
 * Fetches TWCG-1489 connector onboarding state from userPreferences.onboardingState
 * via AGG. Uses the non-deprecated userPreferences path so the preloaded cache entry
 * matches the hook's request exactly, avoiding a redundant network round-trip.
 */
export function useConnectorOnboardingState({
	skip,
}: { skip?: boolean } = {}): UseConnectorOnboardingStateResult {
	const isLicensed = useIsLicensed();

	const { data, loading, error } = useQueryInterop<
		useConnectorOnboardingStateQuery$data,
		useConnectorOnboardingStateQuery$variables
	>(connectorOnboardingStateQuery, {
		variables: {},
		skip: skip || !isLicensed,
		interopApolloAggClient: true,
		fetchPolicy: 'cache-first',
	});

	const entries = data?.userPreferences?.onboardingState ?? EMPTY_ENTRIES;
	const localOnboardingStateSnapshot = useSyncExternalStore(
		subscribeToLocalOnboardingState,
		getLocalOnboardingStateSnapshot,
		getLocalOnboardingStateSnapshot,
	);

	const updateOnboardingState = useCallback(({ key, value }: ConnectorOnboardingStateUpdate) => {
		switch (key) {
			case TWCG_1489_CONNECTOR_ONBOARDING.ENROLMENT_DATE:
				setLocalOnboardingState({ ...localOnboardingState, enrolmentDate: value });
				break;
			case TWCG_1489_CONNECTOR_ONBOARDING.SPOTLIGHT_SHOWN:
				setLocalOnboardingState({ ...localOnboardingState, spotlightShown: value === 'true' });
				break;
			case TWCG_1489_CONNECTOR_ONBOARDING.PROMO_DISMISSED:
				setLocalOnboardingState({
					...localOnboardingState,
					promoDismissed: value as PromoDismissed,
				});
				break;
		}
	}, []);

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
		return { onboardingState: undefined, loading: false, error, updateOnboardingState };
	}

	if (loading) {
		return { onboardingState: undefined, loading: true, updateOnboardingState };
	}

	const findValue = (key: string): string | undefined =>
		entries.find((e) => e.key === key)?.value ?? undefined;

	const queryOnboardingState: ConnectorOnboardingState = {
		enrolmentDate: findValue(TWCG_1489_CONNECTOR_ONBOARDING.ENROLMENT_DATE),
		spotlightShown: findValue(TWCG_1489_CONNECTOR_ONBOARDING.SPOTLIGHT_SHOWN) === 'true',
		promoDismissed: findValue(TWCG_1489_CONNECTOR_ONBOARDING.PROMO_DISMISSED) as
			| PromoDismissed
			| undefined,
	};

	return {
		onboardingState: {
			...queryOnboardingState,
			...localOnboardingStateSnapshot,
		},
		loading: false,
		updateOnboardingState,
	};
}
